import { SlidersHorizontal, X } from "lucide-react";
import { FilterPopover } from "@/components/dashboard/resources-filters/FilterPopover";
import {
  ChipRow,
  ChipToggle,
  FilterSection,
} from "@/components/dashboard/resources-filters/filter-controls";
import { MonthSelect } from "@/components/shared/MonthSelect";
import { SearchInput } from "@/components/shared/search-input";
import {
  activeFilterCount,
  DEFAULT_DEMAND_FILTERS,
  DEMAND_STATUSES,
  type DemandFilterState,
  type DemandStatus,
} from "@/lib/demand-filters";
import { monthsAroundToday } from "@/lib/month-options";
import { formatMonthShort } from "./month-format";

/** Demands run in the past as well as the future, so the filter reaches both ways. */
const FILTER_MONTHS = { back: 24, ahead: 36 } as const;

function toggle(list: DemandStatus[], value: DemandStatus): DemandStatus[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

interface Chip {
  key: string;
  label: string;
  clear: () => void;
}

/** Search + Filters popover (status, date range) + removable chips for the Demands list. */
export function DemandsFilterBar({
  state,
  setState,
  reset,
}: {
  state: DemandFilterState;
  setState: (next: DemandFilterState) => void;
  reset: () => void;
}) {
  const count = activeFilterCount(state);

  const chips: Chip[] = [];
  const statusIsDefault =
    state.statuses.length === DEFAULT_DEMAND_FILTERS.statuses.length &&
    state.statuses.every((s) => DEFAULT_DEMAND_FILTERS.statuses.includes(s));
  if (!statusIsDefault) {
    chips.push({
      key: "status",
      label: state.statuses.length === 0 ? "Any status" : state.statuses.join(", "),
      clear: () => setState({ ...state, statuses: [...DEFAULT_DEMAND_FILTERS.statuses] }),
    });
  }
  if (state.from !== null || state.to !== null) {
    const label = `${state.from ? formatMonthShort(state.from) : "Any"} – ${
      state.to ? formatMonthShort(state.to) : "Any"
    }`;
    chips.push({ key: "dates", label, clear: () => setState({ ...state, from: null, to: null }) });
  }

  const monthValue = (v: string) => (/^\d{4}-(0[1-9]|1[0-2])$/.test(v) ? v : null);

  return (
    <div>
      <div className="flex items-center justify-end gap-2">
        <SearchInput
          value={state.search}
          onValueChange={(v) => setState({ ...state, search: v })}
          placeholder="Search name, Account or ID…"
          className="w-72"
        />
        <FilterPopover
          trigger={() => (
            <>
              <SlidersHorizontal className="size-4" />
              <span>Filters{count > 0 ? ` (${count})` : ""}</span>
            </>
          )}
        >
          {() => (
            <div>
              <FilterSection label="Status (any)">
                <ChipRow>
                  {DEMAND_STATUSES.map((s) => (
                    <ChipToggle
                      key={s}
                      active={state.statuses.includes(s)}
                      onToggle={() => setState({ ...state, statuses: toggle(state.statuses, s) })}
                    >
                      {s}
                    </ChipToggle>
                  ))}
                </ChipRow>
                <p className="mt-1.5 text-xs text-muted-foreground">
                  None selected shows every status.
                </p>
              </FilterSection>
              <FilterSection label="Running between">
                <div className="flex items-center gap-2">
                  <MonthSelect
                    aria-label="From month"
                    size="sm"
                    value={state.from}
                    options={monthsAroundToday({ ...FILTER_MONTHS, selected: state.from })}
                    noneLabel="Any"
                    onValueChange={(next) => setState({ ...state, from: monthValue(next) })}
                  />
                  <span className="text-xs text-muted-foreground">to</span>
                  <MonthSelect
                    aria-label="To month"
                    size="sm"
                    value={state.to}
                    options={monthsAroundToday({ ...FILTER_MONTHS, selected: state.to }).filter(
                      (o) => state.from === null || o.value >= state.from,
                    )}
                    noneLabel="Any"
                    onValueChange={(next) => setState({ ...state, to: monthValue(next) })}
                  />
                </div>
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Keeps Demands whose dates overlap these months. No end date counts as ongoing.
                </p>
              </FilterSection>
            </div>
          )}
        </FilterPopover>
      </div>

      {chips.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-end gap-1.5">
          {chips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-sm font-medium text-foreground"
            >
              {c.label}
              <button
                type="button"
                onClick={c.clear}
                aria-label={`Remove ${c.label}`}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={reset}
            className="text-sm text-muted-foreground underline-offset-2 hover:underline"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
